import * as React from 'react';
import { Text, Icon, mergeStyleSets, getTheme } from '@fluentui/react';

const theme = getTheme();
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const classes = mergeStyleSets({
  card: {
    background: theme.palette.white,
    borderRadius: 2,
    boxShadow: theme.effects.elevation8,
    border: `1px solid ${theme.palette.neutralLight}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    cursor: 'default',
    userSelect: 'none',
  },
  summary: { color: theme.palette.neutralSecondary, marginLeft: 8 },
  chevronBtn: {
    marginLeft: 'auto',
    color: theme.palette.neutralPrimary,
    background: 'transparent',
    border: 'none',
    padding: 6,
    lineHeight: 0,
    cursor: 'pointer',
    selectors: { '&:focus': { outline: `2px solid ${theme.palette.themePrimary}` } },
  },
  body: { padding: '0 12px 12px 12px' },
  collapse: {
    overflow: 'hidden',
    transition: `max-height 640ms ${EASE}, opacity 420ms ease, transform 640ms ${EASE}`,
    willChange: 'max-height, opacity, transform',
  },
});

const Collapse: React.FC<{ open: boolean; children: React.ReactNode }> = ({ open, children }) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = React.useState<number>(open ? 9999 : 0);
  const [opacity, setOpacity] = React.useState<number>(open ? 1 : 0);
  const [y, setY] = React.useState<number>(open ? 0 : -4);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (open) {
      const h = el.scrollHeight;
      setMaxH(h);
      const t1 = requestAnimationFrame(() => {
        setOpacity(1);
        setY(0);
      });
      const t2 = setTimeout(() => setMaxH(9999), 480);
      return () => {
        cancelAnimationFrame(t1);
        clearTimeout(t2);
      };
    } else {
      const h = el.scrollHeight;
      setMaxH(h);
      requestAnimationFrame(() => {
        setOpacity(0);
        setY(-4);
        requestAnimationFrame(() => setMaxH(0));
      });
    }
  }, [open, children]);

  return (
    <div className={classes.collapse} style={{ maxHeight: maxH, opacity, transform: `translateY(${y}px)` }}>
      <div ref={ref}>{children}</div>
    </div>
  );
};

type StepCardProps = {
  title: string;
  summary?: string;
  headerDisplay?: boolean;
  open?: boolean;                // controlado (opcional)
  defaultOpen?: boolean;         // no-controlado (opcional)
  onToggle?: (nextOpen: boolean) => void;
  onEdit?: () => void;
  children: React.ReactNode;
  index?: number;                // compat
};

export const StepCard: React.FC<StepCardProps> = ({
  title,
  summary,
  headerDisplay = true,
  open, // si viene, es controlado
  defaultOpen = false,
  onToggle,
  onEdit,
  children,
}) => {
  // Controlado / no-controlado
  const [internalOpen, setInternalOpen] = React.useState<boolean>(defaultOpen);
  const isControlled = typeof open === 'boolean';
  const isOpen = isControlled ? (open as boolean) : internalOpen;

  const setOpen = (next: boolean) => {
    if (isControlled) {
      onToggle?.(next);
    } else {
      setInternalOpen(next);
      onToggle?.(next);
    }
  };
  const toggle = () => setOpen(!isOpen);

  // A11y
  const bodyId = React.useId();

  const handleChevronClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  const handleChevronKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    }
  };

  return (
    <div className={classes.card} aria-expanded={isOpen}>
      {headerDisplay && (
        <div className={classes.header} onClick={onEdit} role="heading" aria-level={3}>
          <Text variant="medium">{title}</Text>
          {!isOpen && summary && (
            <Text variant="mediumPlus" className={classes.summary} styles={{ root: { fontWeight: 600 } }}>
              {summary}
            </Text>
          )}
          <button
            type="button"
            aria-label={isOpen ? 'Colapsar' : 'Expandir'}
            aria-expanded={isOpen}
            aria-controls={bodyId}
            className={classes.chevronBtn}
            onClick={handleChevronClick}
            onKeyDown={handleChevronKey}
          >
            <Icon iconName={isOpen ? 'ChevronDown' : 'ChevronUp'} />
          </button>
        </div>
      )}

      <Collapse open={isOpen}>
        <div id={bodyId} className={classes.body}>
          {children}
        </div>
      </Collapse>
    </div>
  );
};
